import {
  useCreate,
  useDelete,
  useList,
  useInvalidate,
  BaseRecord,
} from '@refinedev/core'
import { useForm, Controller } from 'react-hook-form'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  FormControl,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Chip,
} from '@mui/material'
import { useState } from 'react'
import { UUID } from 'crypto'
import { ShowButton } from '@refinedev/mui'

interface OrgChildManagementProps {
  orgUuid: UUID
}


interface OrgChildrenFormData {
  parent_uuid: string
  child_uuids: BaseRecord[]
}

export default function OrgChildrenManagement({
  orgUuid,
}: OrgChildManagementProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<OrgChildrenFormData>()

  const { data: orgOptions } = useList({
    resource: 'child-org-options',
    pagination: { pageSize: 100 },
    queryOptions: {
      enabled: !!orgUuid,
    },
    errorNotification: (error) => {
      return {
        message: 'Error getting orgs',
        description: error?.message || 'An error occurred while fetching orgs',
        type: 'error',
      }
    },
    sorters: [{ field: 'name', order: 'asc' }],
  })

  const { data: orgChildrenData, isLoading: orgChildrenDataIsLoading } =
    useList({
      resource: 'org-children',
      meta: {
        org_uuid: orgUuid,
      },
      queryOptions: {
        enabled: !!orgUuid,
      },
      errorNotification: (error) => {
        return {
          message: 'Error getting organisation child orgs',
          description:
            error?.message || 'An error occurred while fetching child orgs',
          type: 'error',
        }
      },
    })

  const { mutate: createChildOrg } = useCreate({
    errorNotification: (error) => {
      return {
        message: 'Error adding child to parent organisation',
        description:
          error?.message ||
          'An error occurred while adding child to parent organisation',
        type: 'error',
      }
    },
  })

  const { mutate: deleteOrgChild } = useDelete()
  const invalidate = useInvalidate()

  const handleCreate = (data: OrgChildrenFormData) => {
    createChildOrg(
      {
        resource: 'org-children',
        values: {
          parent_uuid: orgUuid,
          child_uuids: data.child_uuids.map((child) => child.uuid),
        },
      },
      {
        onSuccess: () => {
          invalidate({
            resource: 'org-children',
            invalidates: ['list'],
          })
          handleCloseDialog()
        },
      }
    )
  }

  const handleDelete = (childUuid: UUID) => {
    if (
      window.confirm(
        'Are you sure you want to remove this child organsisation from the parent organisation?'
      )
    ) {
      deleteOrgChild(
        {
          resource: 'org-children',
          id: `${orgUuid}/${childUuid}`,
          errorNotification: (error) => {
            return {
              message: 'Error removing child from parent organisation',
              description:
                error?.message ||
                'An error occurred while removing child from parent organisation',
              type: 'error',
            }
          },
        },
        {
          onSuccess: () => {
            invalidate({
              resource: 'org-children',
              invalidates: ['list'],
            })
          },
        }
      )
    }
  }

  const handleOpenDialog = () => {
    reset({
      child_uuids: [],
    })
    setIsDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setIsDialogOpen(false)
    reset()
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Organisation Children</Typography>
        <Button variant="contained" color="primary" onClick={handleOpenDialog}>
          Add Child Org
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="70%">Name</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!orgChildrenData || orgChildrenDataIsLoading ? (
              <TableRow>
                <TableCell colSpan={2} align="center">
                  Loading child orgs ...
                </TableCell>
              </TableRow>
            ) : orgChildrenData.data && orgChildrenData.data.length > 0 ? (
              orgChildrenData.data.map((orgChild) => (
                <TableRow key={orgChild.uuid}>
                  <TableCell>
                    {orgChild.name}{' '}
                    <ShowButton hideText recordItemId={orgChild.uuid} />
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => handleDelete(orgChild.uuid)}
                    >
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={2} align="center">
                  No child orgs found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Child Organisation to Parent Organisation</DialogTitle>
        <form onSubmit={handleSubmit(handleCreate)}>
          <DialogContent>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Controller
                name="child_uuids"
                control={control}
                defaultValue={[]}
                rules={{
                  required: 'At least one child organisation is required',
                }}
                render={({ field, fieldState, formState }) => (
                  <Autocomplete
                    {...field}
                    multiple
                    value={field.value || []}
                    onChange={(event, value) => field.onChange(value)}
                    options={orgOptions?.data || []}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) =>
                      option.uuid === value.uuid
                    }
                    filterOptions={(options, params) => {
                      const inputValue = params.inputValue.toLowerCase().trim()
                      if (!inputValue) return options

                      const filtered = options.filter((option) => {
                        return option.name
                          .toLowerCase()
                          .trim()
                          .includes(inputValue)
                      })
                      return filtered
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search orgs"
                        placeholder="Search orgs"
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index })
                        return (
                          <Chip key={key} label={option.name} {...tagProps} />
                        )
                      })
                    }
                  />
                )}
              />
              {errors.child_uuids && (
                <Typography color="error" variant="body2">
                  {errors.child_uuids.message?.toString() ||
                    'Error adding child to parent org'}
                </Typography>
              )}
            </FormControl>
          </DialogContent>
          <DialogActions sx={{ mb: 2, mr: 2 }}>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary">
              Add Child Org
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  )
}
